import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Drawer, Typography, IconButton, Divider, List, ListItemButton,
  ListItemText, ListItemIcon, CircularProgress, Button, Chip, Stack, Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import AndroidIcon from "@mui/icons-material/Android";
import GetAppIcon from "@mui/icons-material/GetApp";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import {
  useGetParentHelpArticlesQuery,
  useGetParentHelpArticleQuery,
} from "../../Api/attoDeskApi";
import { useInstallPrompt } from "../../Core/hook/useInstallPrompt";

/**
 * Parent Portal Help drawer. Right-side drawer that fetches the
 * parent-visible published Help articles (see GET /api/parent-portal/help/*).
 *
 * Two views:
 *   - list of articles grouped by category
 *   - single-article view when the user picks one
 *
 * Includes a small install/Add-to-Home-Screen affordance:
 *   - Chromium browsers: real Install button backed by beforeinstallprompt
 *   - iOS Safari (no API): text-only instructions
 * Never shows a broken/fake install button.
 */
const ParentHelpDrawer: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [activeSlug, setActiveSlug] = useState<string | null>(null);

  // Fetch list only when drawer is open; article only when a slug is picked.
  const { data: listResp, isFetching: listLoading } = useGetParentHelpArticlesQuery(
    {}, { skip: !open },
  );
  const { data: articleResp, isFetching: articleLoading } = useGetParentHelpArticleQuery(
    { slug: activeSlug || "" }, { skip: !open || !activeSlug },
  );

  const items: any[] = Array.isArray((listResp as any)?.data?.items)
    ? (listResp as any).data.items : [];
  const article: any = (articleResp as any)?.data?.article || null;

  // Group by category for a clean list.
  const grouped = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const a of items) {
      const cat = a.category || "General";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(a);
    }
    return Array.from(map.entries());
  }, [items]);

  // Reset article view when drawer closes so re-opening starts from the list.
  useEffect(() => {
    if (!open) setActiveSlug(null);
  }, [open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 460 }, maxWidth: "100vw" } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box sx={{
          px: 2.5, py: 2, display: "flex", alignItems: "center", gap: 1.5,
          borderBottom: "1px solid rgba(15,23,42,0.08)",
        }}>
          {activeSlug ? (
            <IconButton onClick={() => setActiveSlug(null)} size="small" aria-label="Back to Help list">
              <ArrowBackIcon />
            </IconButton>
          ) : (
            <HelpOutlineIcon sx={{ color: "text.secondary" }} />
          )}
          <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 700 }}>
            {activeSlug ? (article?.title || "Loading…") : "Help & Guides"}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close Help">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {activeSlug ? (
            <ArticleView loading={articleLoading} article={article} />
          ) : (
            <ListView
              loading={listLoading}
              grouped={grouped}
              onPick={setActiveSlug}
            />
          )}
        </Box>

        {/* Footer: install / add-to-home-screen shortcut */}
        {!activeSlug && (
          <Divider />
        )}
        {!activeSlug && (
          <InstallFooter onOpenHomeScreenArticle={() => setActiveSlug("parent-add-to-home-screen")} />
        )}
      </Box>
    </Drawer>
  );
};

const ListView: React.FC<{ loading: boolean; grouped: [string, any[]][]; onPick: (slug: string) => void }> = ({ loading, grouped, onPick }) => {
  if (loading && grouped.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  if (!loading && grouped.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
        <Typography sx={{ fontSize: 13 }}>No parent guides are available yet.</Typography>
      </Box>
    );
  }
  return (
    <Box>
      {grouped.map(([cat, arr]) => (
        <Box key={cat}>
          <Typography sx={{
            px: 2.5, pt: 2, pb: 0.5, fontSize: 11, fontWeight: 800,
            color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em",
          }}>{cat}</Typography>
          <List dense disablePadding>
            {arr.map((a: any) => (
              <ListItemButton key={a.slug} onClick={() => onPick(a.slug)} sx={{ py: 1, px: 2.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <ArticleOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                </ListItemIcon>
                <ListItemText
                  primary={<Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{a.title}</Typography>}
                  secondary={a.summary ? <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{a.summary}</Typography> : null}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      ))}
    </Box>
  );
};

const ArticleView: React.FC<{ loading: boolean; article: any }> = ({ loading, article }) => {
  if (loading || !article) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  const qs: any[] = Array.isArray(article.quick_steps) ? article.quick_steps : [];
  const body: string = article.body_markdown || "";
  return (
    <Box sx={{ p: 2.5 }}>
      {article.summary && (
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", mb: 2 }}>
          {article.summary}
        </Typography>
      )}
      {qs.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: "text.secondary", mb: 0.75, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Quick Steps
          </Typography>
          <Stack spacing={0.75}>
            {qs.map((s: any, i: number) => (
              <Box key={i} sx={{ display: "flex", gap: 1.25 }}>
                <Chip label={s.number ?? i + 1} size="small"
                  sx={{ minWidth: 26, height: 22, fontWeight: 800, fontSize: 11 }} />
                <Typography sx={{ fontSize: 13, color: "text.primary", flex: 1, lineHeight: 1.5 }}>
                  {s.description}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
      {body && (
        <Box>
          <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: "text.secondary", mb: 0.75, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Details
          </Typography>
          {/* Simple markdown-lite rendering — the seed content uses only
              plain paragraphs, ordered lists and **bold**. Anything more
              complex would deserve a real markdown renderer. */}
          <MarkdownLite text={body} />
        </Box>
      )}
    </Box>
  );
};

const MarkdownLite: React.FC<{ text: string }> = ({ text }) => {
  const rendered = useMemo(() => {
    // Split into paragraphs on blank lines, keep ordered-list numbering,
    // convert **bold**, and preserve headings ("### Heading").
    return text.split(/\n{2,}/).map((block, i) => {
      if (/^###\s+/.test(block)) {
        return <Typography key={i} sx={{ fontSize: 13.5, fontWeight: 800, mt: 1.5, mb: 0.5 }}>{block.replace(/^###\s+/, "")}</Typography>;
      }
      // ordered list
      if (/^\d+\.\s+/.test(block)) {
        const items = block.split(/\n(?=\d+\.\s+)/).map((s) => s.replace(/^\d+\.\s+/, ""));
        return (
          <Box component="ol" key={i} sx={{ pl: 3, m: 0, mb: 1.25 }}>
            {items.map((it, j) => (
              <li key={j}><Typography component="span" sx={{ fontSize: 13, lineHeight: 1.55 }} dangerouslySetInnerHTML={{ __html: bold(it) }} /></li>
            ))}
          </Box>
        );
      }
      // unordered list
      if (/^-\s+/.test(block)) {
        const items = block.split(/\n(?=-\s+)/).map((s) => s.replace(/^-\s+/, ""));
        return (
          <Box component="ul" key={i} sx={{ pl: 3, m: 0, mb: 1.25 }}>
            {items.map((it, j) => (
              <li key={j}><Typography component="span" sx={{ fontSize: 13, lineHeight: 1.55 }} dangerouslySetInnerHTML={{ __html: bold(it) }} /></li>
            ))}
          </Box>
        );
      }
      return <Typography key={i} sx={{ fontSize: 13, lineHeight: 1.55, mb: 1.25 }} dangerouslySetInnerHTML={{ __html: bold(block.replace(/\n/g, "<br/>")) }} />;
    });
  }, [text]);
  return <>{rendered}</>;
};

// Minimal bold-only markdown expansion. Everything is escaped first so
// nothing user-supplied can inject HTML.
const bold = (s: string): string => {
  const esc = s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return esc.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
};

const InstallFooter: React.FC<{ onOpenHomeScreenArticle: () => void }> = ({ onOpenHomeScreenArticle }) => {
  const { canPrompt, isIOS, prompt } = useInstallPrompt();
  return (
    <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5, bgcolor: "rgba(15,23,42,0.02)" }}>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "text.primary" }}>
          Add Parent Portal to your Home Screen
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.25 }}>
          {canPrompt
            ? "Install as a shortcut for faster access."
            : isIOS
              ? "iPhone / iPad: Share → Add to Home Screen."
              : "Chrome menu → Install app / Add to Home screen."}
        </Typography>
      </Box>
      {canPrompt ? (
        <Tooltip title="Install Parent Portal">
          <Button variant="contained" size="small" startIcon={<GetAppIcon />}
            onClick={prompt} sx={{ textTransform: "none" }}>
            Install
          </Button>
        </Tooltip>
      ) : (
        <Tooltip title="Show instructions">
          <IconButton onClick={onOpenHomeScreenArticle} size="small" aria-label="Show Add to Home Screen instructions">
            {isIOS ? <PhoneIphoneIcon /> : <AndroidIcon />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default ParentHelpDrawer;
